"use client";

import {
  Badge,
  Box,
  SimpleGrid,
  Stack,
  type Tokens,
} from "@chakra-ui/react";
import type { IdentifiedTopic } from "@/generated/fastapi";
import WithControlButtons from "@/shared/ui/actions/WithControlButtons";
import { stringifyIdentifiedTopic } from "../../utils";
import type { UpdateTopicInput } from "./useIdentifiedTopicsState";
import useTopicReviewActions from "./useTopicReviewActions";

type TopicReviewItemProps = {
  topic: IdentifiedTopic;
  textSize?: Tokens["fontSizes"];
  removeTopic: () => void;
  updateTopic: (update: UpdateTopicInput) => void;
  setProposedPost: (post: string | null) => void;
  inConfirmationAfterExtraction: boolean;
};

export default function TopicReviewItem({
  topic,
  textSize = "sm",
  removeTopic,
  updateTopic,
  setProposedPost,
  inConfirmationAfterExtraction,
}: TopicReviewItemProps) {
  const {
    handleConfirmTopicClick,
    handleDeleteTopicClick,
    handleGenerateQuestionsClick,
    isGeneratingQuestions,
    handleProposePostClick,
    isProposingPost,
  } = useTopicReviewActions({
    updateTopic: (_topicId: string, update: UpdateTopicInput) => {
      updateTopic(update);
    },
    setProposedPost,
  });

  const targetMetaInfo = [
    {
      label: "Status",
      value: topic.confirmed_at ? "Confirmed" : "Unconfirmed",
      color: topic.confirmed_at ? "moveon.processed" : "moveon.unprocessed",
    },
    { label: "Aim", value: topic.aim },
    {
      label: "Values",
      value: topic.communicated_values?.join(", "),
    },
    { label: "Confidence", value: topic.confidence },
  ].filter((field) => field.value);

  return (
    <Box>
      <WithControlButtons
        primary={
          inConfirmationAfterExtraction
            ? {
                onClick: async () =>
                  await handleConfirmTopicClick(
                    {
                      topic_id: topic.id,
                      should_confirm: true,
                    },
                    () => {
                      if (inConfirmationAfterExtraction) {
                        removeTopic();
                      }
                    },
                  ),
                icon: "confirm",
                humanReadable: "Confirm topic",
              }
            : undefined
        }
        secondary={
          !inConfirmationAfterExtraction
            ? {
                onClick: async () =>
                  await handleGenerateQuestionsClick(
                    {
                      origin_id: topic.origin_id,
                      topic_id: topic.id,
                    },
                    () => {
                      if (inConfirmationAfterExtraction) {
                        removeTopic();
                      }
                    },
                  ),
                icon: "generate",
                loading: isGeneratingQuestions,
                humanReadable: "Generate prompting questions",
              }
            : undefined
        }
        tertiary={
          !inConfirmationAfterExtraction
            ? {
                onClick: async () =>
                  await handleProposePostClick({
                    origin_id: topic.origin_id,
                    topic_id: topic.id,
                  }),
                icon: "write",
                loading: isProposingPost,
                humanReadable: "Propose post draft",
              }
            : undefined
        }
        delete={{
          onClick: async () =>
            await handleDeleteTopicClick(
              {
                topic_ids: [topic.id],
                delete_mode: "permanent",
              },
              () => {
                removeTopic();
              },
            ),
          mode: "permanent",
          humanReadable: "Delete topic permanently",
        }}
        clipboard={{
          value: stringifyIdentifiedTopic(topic),
          humanReadable: "Copy topic details to clipboard",
        }}
        loading={{
          show: isGeneratingQuestions || isProposingPost,
          message: isGeneratingQuestions
            ? "Generating questions..."
            : "Proposing post...",
        }}
      >
        <Stack spaceY="4">
          <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }}>
            {targetMetaInfo.map((field) => (
              <Box key={field.label}>
                <Badge
                  mb="1"
                  // @ts-ignore Chakra UI typing issue with bg
                  bg={field.color ? `${field.color}` : undefined}
                >
                  {field.label}
                </Badge>
                <Box fontSize={textSize}>{field.value}</Box>
              </Box>
            ))}
          </SimpleGrid>

          <Box>
            <Badge mb="1">Topic</Badge>
            <Box fontSize={textSize} mb="2">
              {topic.topic}
            </Box>

            <Badge mb="1">Headline</Badge>
            <Box fontSize={textSize} mb="2">
              {topic.headline}
            </Box>

            <Badge mb="1">Claim</Badge>
            <Box fontSize={textSize} mb="2">
              {topic.claim}
            </Box>

            <Badge mb="1">Content Outline</Badge>

            <Box fontSize={textSize} pl="4" listStyleType="decimal">
              {topic.content_outline?.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </Box>

            <Badge mb="1" mt="4">
              Prompting Questions
            </Badge>
            {topic.prompting_questions &&
            topic.prompting_questions.length > 0 ? (
              <Box fontSize={textSize} pl="4" listStyleType="decimal">
                {topic.prompting_questions.map((question, index) => (
                  <li key={index}>{question}</li>
                ))}
              </Box>
            ) : (
              <Box fontSize={textSize} fontStyle="italic">
                No prompting questions available. Click the button (re)generate
                them.
              </Box>
            )}
          </Box>
        </Stack>
      </WithControlButtons>
    </Box>
  );
}
